import { ReactNode, useMemo } from 'react'

import { NotamProps } from '@/domain/models/Notam'

import {
  DateTimeContainer,
  DateTimeText,
  IconTextContainer,
  LimitsContainer,
  LimitText,
  PeriodContainer,
  PillContainer,
  PillText,
  Text,
  TextContainer,
  TypeContainer
} from './NotamItem.styles'

import LowerLimitIcon from '@/assets/lower-limit.svg?react'
import UpperLimitIcon from '@/assets/upper-limit.svg?react'
import { useI18n } from '@/presentation/hooks/useI18n'

interface NotamItemProps {
  notam: NotamProps
  actions?: ReactNode
  tone?: 'default' | 'adhpInfo'
}

function NotamItem({ notam, actions, tone = 'default' }: NotamItemProps) {
  const { t, lang, getStandardizedDateTimeUTC } = useI18n()
  const deviceLanguage = lang()
  const upperLimitText = notam.upperLimitText ? notam.upperLimitText : 'UNL'
  const lowerLimitText = notam.lowerLimitText ? notam.lowerLimitText : 'GND'

  const notamText = useMemo(() => {
    const isNotamInDeviceLanguage = notam.language === deviceLanguage
    if (isNotamInDeviceLanguage) {
      return notam.text
    }
    return notam.textEn || notam.text
  }, [notam.language, notam.text, notam.textEn, deviceLanguage])

  return (
    <>
      <TypeContainer>
        <PillContainer type={notam.type} $tone={tone}>
          <PillText>{notam.identifier}</PillText>
        </PillContainer>
        <PillContainer type={notam.type} $tone={tone}>
          <PillText>{notam.type === 'NOTAMR' ? 'R' : 'N'}</PillText>
        </PillContainer>
        {notam.type === 'NOTAMR' && notam.replacesNotamIdentifier && (
          <PillContainer type={notam.type} variant="dark" $tone={tone}>
            <PillText>{notam.replacesNotamIdentifier}</PillText>
          </PillContainer>
        )}
        <DateTimeContainer>
          <DateTimeText>{getStandardizedDateTimeUTC(new Date(notam.dispatchedAt))}</DateTimeText>
        </DateTimeContainer>
      </TypeContainer>
      <TextContainer>
        <Text>{notamText}</Text>
      </TextContainer>
      {(notam.lowerLimitText || notam.upperLimitText) && (
        <LimitsContainer>
          <IconTextContainer>
            <LowerLimitIcon width={16} height={16} style={{ marginRight: 4 }} />
            <LimitText>{lowerLimitText}</LimitText>
          </IconTextContainer>
          <IconTextContainer>
            <UpperLimitIcon width={16} height={16} style={{ marginRight: 4 }} />
            <LimitText>{upperLimitText}</LimitText>
          </IconTextContainer>
        </LimitsContainer>
      )}
      <PeriodContainer>
        <DateTimeText>
          {notam.validUntil
            ? t('AERONAUTICAL-INFORMATION_NOTAM_PERIOD', {
                startDate: getStandardizedDateTimeUTC(new Date(notam.validFrom)),
                endDate: getStandardizedDateTimeUTC(new Date(notam.validUntil))
              })
            : `${getStandardizedDateTimeUTC(new Date(notam.validFrom))} a PERM`}
        </DateTimeText>
      </PeriodContainer>
      {notam.activePeriods && (
        <PeriodContainer>
          <DateTimeText>{notam.activePeriods}</DateTimeText>
        </PeriodContainer>
      )}
      {actions && actions}
    </>
  )
}

export { NotamItem }
